import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Divider
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Google as GoogleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

const ApiKeyManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState("");
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    GOOGLE_CLIENT_ID: "",
    GOOGLE_CLIENT_SECRET: "",
    label: "",
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  // Check if user is boss
  const isBoss = user?.role === 'boss';

  useEffect(() => {
    if (isBoss) {
      fetchTokens();
    }
  }, [isBoss]);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/google-tokens`);
      setTokens(response.data.tokens || []);
      setError("");
    } catch (error) {
      console.error('Error fetching tokens:', error);
      setError('Failed to fetch Google tokens');
      toast.error('Failed to fetch Google tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToken = () => {
    setFormData({
      GOOGLE_CLIENT_ID: "",
      GOOGLE_CLIENT_SECRET: "",
      label: "",
      isActive: true
    });
    setSelectedToken(null);
    setAddDialogOpen(true);
  };

  const handleEditToken = (token) => {
    setFormData({
      GOOGLE_CLIENT_ID: token.GOOGLE_CLIENT_ID || "",
      GOOGLE_CLIENT_SECRET: token.GOOGLE_CLIENT_SECRET || "",
      label: token.label || "",
      isActive: token.isActive !== false
    });
    setSelectedToken(token);
    setEditDialogOpen(true);
  };

  const handleDeleteToken = (token) => {
    setSelectedToken(token);
    setDeleteDialogOpen(true);
  };

  const handleSaveToken = async () => {
    if (!formData.GOOGLE_CLIENT_ID || !formData.GOOGLE_CLIENT_SECRET) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const updatedTokens = [...tokens];
      
      if (selectedToken) {
        // Edit existing token
        const index = updatedTokens.findIndex(t => t.index === selectedToken.index);
        if (index !== -1) {
          updatedTokens[index] = {
            ...updatedTokens[index],
            ...formData
          };
        }
      } else {
        // Add new token
        const newToken = {
          ...formData,
          index: updatedTokens.length
        };
        updatedTokens.push(newToken);
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/google-tokens`, {
        tokens: updatedTokens
      });

      toast.success(selectedToken ? 'Token updated successfully' : 'Token added successfully');
      setAddDialogOpen(false);
      setEditDialogOpen(false);
      fetchTokens();
    } catch (error) {
      console.error('Error saving token:', error);
      toast.error('Failed to save token');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedToken) return;

    setSaving(true);
    try {
      const updatedTokens = tokens.filter(t => t.index !== selectedToken.index);
      
      await axios.post(`${process.env.REACT_APP_API_URL}/google-tokens`, {
        tokens: updatedTokens
      });

      toast.success('Token deleted successfully');
      setDeleteDialogOpen(false);
      fetchTokens();
    } catch (error) {
      console.error('Error deleting token:', error);
      toast.error('Failed to delete token');
    } finally {
      setSaving(false);
    }
  };

  const toggleSecretVisibility = (index) => {
    setShowSecrets(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const maskSecret = (secret) => {
    if (!secret) return '';
    return secret.length > 8 
      ? secret.substring(0, 4) + '•'.repeat(secret.length - 8) + secret.substring(secret.length - 4)
      : '•'.repeat(secret.length);
  };

  if (!isBoss) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You must be a boss to access API key management.</Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            API Key Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage Google OAuth tokens for Gmail API integration. These tokens are used by all users when connecting their Gmail accounts.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GoogleIcon color="primary" />
                <Box>
                  <Typography variant="h6">{tokens.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Tokens
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="h6">
                    {tokens.filter(t => t.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Tokens
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ErrorIcon color="error" />
                <Box>
                  <Typography variant="h6">
                    {tokens.filter(t => !t.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Tokens
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Google OAuth Tokens</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddToken}
          sx={{ 
            backgroundColor: 'primary.main',
            '&:hover': { backgroundColor: 'primary.dark' }
          }}
        >
          Add Token
        </Button>
      </Box>

      {/* Tokens Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Index</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Client ID</TableCell>
              <TableCell>Client Secret</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No Google tokens found. Click "Add Token" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tokens.map((token, index) => (
                <TableRow key={token.index || index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {token.index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {token.label || `Token ${token.index + 1}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {token.GOOGLE_CLIENT_ID}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {showSecrets[token.index] ? token.GOOGLE_CLIENT_SECRET : maskSecret(token.GOOGLE_CLIENT_SECRET)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => toggleSecretVisibility(token.index)}
                      >
                        {showSecrets[token.index] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={token.isActive ? 'Active' : 'Inactive'}
                      color={token.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditToken(token)}
                        color="primary"
                        title="Edit token"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteToken(token)}
                        color="error"
                        title="Delete token"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Token Dialog */}
      <Dialog
        open={addDialogOpen || editDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedToken ? 'Edit Google Token' : 'Add Google Token'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token Label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., Production Token, Development Token"
                  helperText="A friendly name to identify this token"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Google Client ID"
                  value={formData.GOOGLE_CLIENT_ID}
                  onChange={(e) => setFormData({ ...formData, GOOGLE_CLIENT_ID: e.target.value })}
                  placeholder="your-client-id.apps.googleusercontent.com"
                  required
                  helperText="Get this from Google Cloud Console > APIs & Services > Credentials"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Google Client Secret"
                  value={formData.GOOGLE_CLIENT_SECRET}
                  onChange={(e) => setFormData({ ...formData, GOOGLE_CLIENT_SECRET: e.target.value })}
                  placeholder="your-client-secret"
                  required
                  type="password"
                  helperText="Keep this secret secure. Never share it publicly."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  Inactive tokens cannot be used for new connections
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              setEditDialogOpen(false);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveToken}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : (selectedToken ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Google Token</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Google token?
            <br />
            <br />
            <strong>Token:</strong> {selectedToken?.label || `Token ${(selectedToken?.index || 0) + 1}`}
            <br />
            <strong>Client ID:</strong> {selectedToken?.GOOGLE_CLIENT_ID}
            <br />
            <br />
            This action cannot be undone. Users currently using this token will need to reconnect their Gmail accounts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeyManagement;
